import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { memo, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ListingCard from '../../components/BasicUI/ListingCard/ListingCard';
import ListingCardSkeleton from '../../components/BasicUI/ListingCard/ListingCardSkeleton';
import Swiper from '../../components/Swiper/Swiper';
import { petrol } from '../../styles/mui/colors';
import useScreenSize from '../../util/hooks/useScreenSize';
import { NEARBY_MIN } from './ListingPage.duck';
import ListingSection from './ListingSection';
import ListingSectionItem from './ListingSectionItem';

// Arrows overlay the card images and sit just inside the carousel edges (the
// strip is full-bleed, so the slick defaults of left/right: -25px fall off
// screen). `top` is set per render to the measured image centre.
const carouselSx = (arrowTop) => ({
  position: 'relative',
  '& .slick-slider .slick-prev, & .slick-slider .slick-next': {
    top: arrowTop != null ? `${arrowTop}px` : '32%',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
    color: petrol.main,
    zIndex: 2,
    transition: 'background-color 0.2s ease',
  },
  '& .slick-slider .slick-prev:hover, & .slick-slider .slick-next:hover': {
    backgroundColor: '#fff',
    color: petrol.main,
  },
  '& .slick-slider .slick-prev': { left: 8 },
  '& .slick-slider .slick-next': { right: 8 },
  '& .slick-slider .slick-arrow svg': { fontSize: 18 },
  // Carousel is non-infinite: hide the arrow that can't scroll further.
  '& .slick-slider .slick-arrow.slick-disabled': { opacity: 0, pointerEvents: 'none' },
});

/**
 * Horizontal carousel of listings near the current listing.
 * Renders skeletons while loading and hides itself entirely when fewer than
 * NEARBY_MIN results are available.
 *
 * @param {object} props
 * @param {Array} props.listings - denormalized nearby listing entities
 * @param {boolean} props.inProgress - whether the nearby fetch is running
 * @returns {React.JSX.Element|null}
 */
const SectionNearbyListingsMaybeComponent = ({ listings = [], inProgress = false }) => {
  const { width } = useScreenSize();

  // Derive the visible card count from the actual viewport. react-slick's own
  // `responsive` breakpoints rely on matchMedia and don't reliably apply after
  // SSR/hydration here, which cram 4 cards into a mobile viewport — so we drive
  // slidesToShow explicitly (values mirror the Swiper breakpoints).
  const slidesToShow = width >= 1200 ? 4 : width >= 900 ? 3 : width >= 600 ? 2 : 1;

  // Centre the arrows on the card image. Card width ≈ container / slidesToShow,
  // image follows the 16/10 aspect ratio, so the image centre is half its height.
  const carouselRef = useRef(null);
  const [arrowTop, setArrowTop] = useState(null);
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const slideWidth = el.clientWidth / slidesToShow;
    const imageHeight = ((slideWidth - 16) * 10) / 16; // 16px px={1} gutters
    setArrowTop(Math.round(imageHeight / 2));
  }, [slidesToShow, width, listings.length, inProgress]);

  const hasEnough = listings.length >= NEARBY_MIN;
  if (!inProgress && !hasEnough) {
    return null;
  }

  const showSkeletons = inProgress && !hasEnough;

  return (
    <ListingSection>
      <ListingSectionItem params={{ size: { lg: 12 } }}>
        <Typography
          component="h3"
          mb={2}
          sx={{ textAlign: { xs: 'center', md: 'left' }, width: '100%' }}
          variant="h4"
        >
          <FormattedMessage id="ListingPage.nearbyListingsTitle" />
        </Typography>
      </ListingSectionItem>
      <ListingSectionItem params={{ size: { lg: 12 } }}>
        <Box ref={carouselRef} sx={carouselSx(arrowTop)} width="100%">
          <Swiper dots={false} infinite={false} slidesToScroll={1} slidesToShow={slidesToShow}>
            {showSkeletons
              ? Array.from({ length: 4 }).map((_, i) => (
                  <Box key={`nearby-skeleton-${i}`} px={1}>
                    <ListingCardSkeleton />
                  </Box>
                ))
              : listings.map((listing) => (
                  <Box key={listing.id.uuid} px={1}>
                    <ListingCard listing={listing} />
                  </Box>
                ))}
          </Swiper>
        </Box>
      </ListingSectionItem>
    </ListingSection>
  );
};

SectionNearbyListingsMaybeComponent.displayName = 'SectionNearbyListingsMaybe';

const SectionNearbyListingsMaybe = memo(SectionNearbyListingsMaybeComponent);

export default SectionNearbyListingsMaybe;
